import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { NotificationConfig, NotificationItem, NotificationType, NotificationPosition } from './notification.models';

/**
 * NotificationService
 *
 * Serviço global para disparar toasts/notificações programaticamente.
 *
 * @example
 * constructor(private notify: NotificationService) {}
 *
 * this.notify.success('Registro salvo com sucesso!');
 * this.notify.error('Falha ao conectar com o servidor.');
 * this.notify.warning('Sua sessão expira em 5 minutos.');
 * this.notify.info('Nova atualização disponível.', { title: 'Novidade' });
 */
@Injectable({ providedIn: 'root' })
export class NotificationService {
  private readonly notificationsSubject = new BehaviorSubject<NotificationItem[]>([]);
  readonly notifications$: Observable<NotificationItem[]> = this.notificationsSubject.asObservable();

  /** Posição global dos toasts */
  position: NotificationPosition = 'top-right';

  private idCounter = 0;

  // ─── Métodos de conveniência ────────────────────────────────────────────────

  success(message: string, config?: Partial<NotificationConfig>): void {
    this.show({ message, type: 'success', duration: 4000, ...config });
  }

  error(message: string, config?: Partial<NotificationConfig>): void {
    this.show({ message, type: 'error', duration: 6000, ...config });
  }

  warning(message: string, config?: Partial<NotificationConfig>): void {
    this.show({ message, type: 'warning', duration: 5000, ...config });
  }

  info(message: string, config?: Partial<NotificationConfig>): void {
    this.show({ message, type: 'info', duration: 4000, ...config });
  }

  // ─── Core ───────────────────────────────────────────────────────────────────

  show(config: NotificationConfig): void {
    const item: NotificationItem = {
      ...config,
      id: `notif_${++this.idCounter}_${Date.now()}`,
      type: config.type || 'info',
      duration: config.duration ?? 4000,
      dismissible: config.dismissible ?? true,
      createdAt: Date.now(),
    };

    const current = this.notificationsSubject.value;
    this.notificationsSubject.next([...current, item]);

    // Auto-dismiss
    if (item.duration && item.duration > 0) {
      setTimeout(() => this.dismiss(item.id), item.duration);
    }
  }

  dismiss(id: string): void {
    const current = this.notificationsSubject.value;
    this.notificationsSubject.next(current.filter((n) => n.id !== id));
  }

  clearAll(): void {
    this.notificationsSubject.next([]);
  }
}
